import { CheckCircleOutlined, CloseOutlined, ExclamationCircleOutlined, YoutubeOutlined } from "@ant-design/icons";
import { Alert, Button, Card, Descriptions, Space, Tag, Tooltip, Typography, message } from "antd";
import { useState } from "react";
import { v4 as uuid } from "uuid";

import { exercisesActions } from "@fitness-app/app-store";
import { ExerciseVideoType } from "@fitness-app/data-models/entities/Exercise";
import { type TrainingExerciseProposal } from "@fitness-app/data-models/entities/TrainingProgramAgent";
import { getError, getErrorMessage } from "@fitness-app/utils";

import {
  EXERCISE_LIBRARY_LOCKED_HINT,
  useCanEditExerciseLibrary,
} from "~/modules/TrainingPrograms/useCanEditExerciseLibrary";
import {
  bodyPartOptions,
  exerciseTypeOptions,
} from "~/modules/TrainingPrograms/tabs/ExercisesDatabase/ExerciseForm/types";
import { useAppDispatch, useAppSelector } from "~/store/initializeStore";

const typeLabels = new Map(exerciseTypeOptions.map((option) => [option.value, option.label]));
const bodyPartLabels = new Map(bodyPartOptions.map((option) => [option.value, option.label]));

export const ExerciseProposalCard = ({
  onSaved,
  proposal,
}: {
  onSaved: (exerciseName: string) => void;
  proposal: TrainingExerciseProposal;
}) => {
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [video, setVideo] = useState(proposal.video);
  const dispatch = useAppDispatch();
  const loggedUser = useAppSelector((store) => store.user.data);
  const canEditLibrary = useCanEditExerciseLibrary();

  const save = async () => {
    if (!proposal.canSave || saving || saved) return;
    setSaving(true);
    try {
      const now = new Date().toISOString();
      await dispatch(
        exercisesActions.addExercise({
          exercise: {
            id: uuid(),
            name: proposal.draft.name.trim(),
            type: proposal.draft.type,
            bodyParts: proposal.draft.bodyParts,
            tags: proposal.draft.tags,
            description: proposal.draft.description || null,
            instruction: proposal.draft.instruction,
            links: [],
            videoType: video ? ExerciseVideoType.YOUTUBE : ExerciseVideoType.NONE,
            videoLink: video?.url ?? null,
            videoId: null,
            iconName: null,
            createdAt: now,
            updatedAt: now,
            createdBy: loggedUser?.id || null,
            updateConnectedExercises: false,
          },
        }),
      ).unwrap();
      setSaved(true);
      void message.success("Ćwiczenie zostało dodane do bazy");
      onSaved(proposal.draft.name.trim());
    } catch (error) {
      void message.error(
        getError(error).message.includes("duplicate key value violates unique constraint")
          ? "Ćwiczenie o tej nazwie już istnieje w bazie"
          : getErrorMessage(error),
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card
      size="small"
      className="mt-3 border-emerald-200 bg-emerald-50/40"
      title={
        <Space>
          {proposal.canSave ? (
            <CheckCircleOutlined className="text-emerald-600" />
          ) : (
            <ExclamationCircleOutlined className="text-amber-600" />
          )}
          <span>Nowe ćwiczenie: {proposal.draft.name}</span>
        </Space>
      }
    >
      <Descriptions size="small" column={2} className="mb-2">
        <Descriptions.Item label="Typ">{typeLabels.get(proposal.draft.type) ?? proposal.draft.type}</Descriptions.Item>
        <Descriptions.Item label="Partie ciała">
          {proposal.draft.bodyParts.map((bodyPart) => bodyPartLabels.get(bodyPart) ?? bodyPart).join(", ")}
        </Descriptions.Item>
        {proposal.draft.tags.length ? (
          <Descriptions.Item label="Tagi" span={2}>
            {proposal.draft.tags.join(", ")}
          </Descriptions.Item>
        ) : null}
      </Descriptions>

      {proposal.draft.description ? (
        <Typography.Paragraph className="mb-2 text-sm">{proposal.draft.description}</Typography.Paragraph>
      ) : null}

      {proposal.draft.instruction.length ? (
        <ol className="mb-2 list-decimal space-y-0.5 pl-5 text-sm">
          {proposal.draft.instruction.map((step, index) => (
            <li key={index}>{step}</li>
          ))}
        </ol>
      ) : null}

      {video ? (
        <div className="mb-2 flex items-center gap-3 rounded-lg border border-gray-200 bg-white p-2">
          <a href={video.url} target="_blank" rel="noreferrer" className="relative shrink-0">
            <img src={video.thumbnailUrl} alt={video.title} className="h-16 w-28 rounded object-cover" />
            <YoutubeOutlined className="absolute inset-0 m-auto h-fit w-fit text-2xl text-red-600" />
          </a>
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm font-medium text-gray-900" title={video.title}>
              {video.title}
            </div>
            <div className="mt-0.5 text-xs text-gray-500">Film YouTube zostanie podpięty do ćwiczenia</div>
          </div>
          {saved ? null : (
            <Tooltip title="Usuń film — ćwiczenie zostanie dodane bez wideo">
              <Button size="small" type="text" icon={<CloseOutlined />} onClick={() => setVideo(null)} />
            </Tooltip>
          )}
        </div>
      ) : null}

      {proposal.similarExercises.length ? (
        <div className="mb-2">
          <Typography.Text type="secondary" className="text-xs">
            Podobne ćwiczenia w bazie:
          </Typography.Text>
          <div className="mt-1 flex flex-wrap gap-1">
            {proposal.similarExercises.map((exercise) => (
              <Tag key={exercise.id}>{exercise.name}</Tag>
            ))}
          </div>
        </div>
      ) : null}

      {proposal.issues.length ? (
        <Alert className="mb-2" type="warning" showIcon message={proposal.issues.join(" ")} />
      ) : null}

      <div className="flex justify-end">
        {saved ? (
          <Tag color="green" icon={<CheckCircleOutlined />}>
            Dodano do bazy
          </Tag>
        ) : (
          <Tooltip title={canEditLibrary ? "" : EXERCISE_LIBRARY_LOCKED_HINT}>
            <Button
              type="primary"
              disabled={!proposal.canSave || !canEditLibrary}
              loading={saving}
              onClick={() => void save()}
            >
              Dodaj ćwiczenie do bazy
            </Button>
          </Tooltip>
        )}
      </div>
    </Card>
  );
};
